import PropTypes from 'prop-types';
import React from 'react';
import { compose } from 'recompose';
import _ from 'lodash';
import { List } from 'immutable';
import withRouter from 'react-router/lib/withRouter';
import { routerShape } from 'react-router/lib/PropTypes';
import Button from 'react-bootstrap/lib/Button';

import urls from "../../urls";
import { HandOffPages } from '../../constants/AppConstants';
import t from '../../lib/I18n';
import connectToStores from '../../helpers/connectToStores';
import DimensionsStore from '../../stores/DimensionsStore';
import User from "../../records/User";
import SeasonSearch from '../../records/SeasonSearch';
import Search from '../../records/Search';
import FavouritesActions from '../../actions/FavouritesActions';
import ConfirmationModalActions from '../../actions/ConfirmationModalActions';

import BaseModal from '../general/BaseModal';
import BAlert from '../general/BAlert';
import FavouriteList from './FavouriteList';
import Calendar from '../form/Calendar';
import SteppableList from '../general/SteppableList';

const _getStateFromStores = () => ({
    isDesktop: DimensionsStore.isDesktopWidth(),
});

function _getSearchFromFavourite (favourite) {
    return favourite.isSeason() ?
        SeasonSearch.fromReorderable(favourite) : Search.fromReorderable(favourite);
}

const CHOOSE_FAVOURITE_STEP = 'choose-favourite';
const CHOOSE_DATE_STEP = 'choose-date';

export class FavouritesModal extends React.PureComponent {

    static propTypes = {
        favourites: PropTypes.instanceOf(List).isRequired,
        user: PropTypes.instanceOf(User).isRequired,
        onCancel: PropTypes.func,
        router: routerShape,
        isDesktop: PropTypes.bool,
    };

    static defaultProps = {
        favourites: new List(),
        onCancel: _.noop,
        isDesktop: true,
    };

    state = {
        selectedFavourite: null,
        date: null,
        error: null,
        currentStep: 1
    };

    steps () {
        return [
            {
                number: 1,
                name: CHOOSE_FAVOURITE_STEP,
                title: 'favourites.modal.step1Title',
                element: this.renderFavourites,
                nextAction: _.noop,
                nextStep: () => CHOOSE_DATE_STEP
            },
            {
                number: 2,
                name: CHOOSE_DATE_STEP,
                title: 'favourites.modal.step2Title',
                element: this.renderCalendar,
                continueTitle: 'favourites.modal.reorder',
                nextDisabled: (state) => !state.date,
                nextStep: () => null,
                nextAction: () => {
                    const { router, onCancel } = this.props;
                    const { selectedFavourite, date } = this.state;
                    const pathname = HandOffPages.FARE;
                    const query = {
                        reorder: selectedFavourite.id,
                        startDate: date.format('YYYY-MM-DD'),
                        useDeliveryPreference: true
                    };
                    router.push(urls.location(pathname, null, query));
                    onCancel();
                }
            }
        ];
    }

    selectFavourite = (favourite) => {
        this.setState({
            selectedFavourite: favourite,
            date: null,
            error: null
        });
        this.nextStep();
    };

    deleteFavourite = (favourite) => {
        ConfirmationModalActions.open(
            () => FavouritesActions.remove(favourite, this.props.user),
            'confirmation-modal.delete-item',
            { icon: 'bin' }
        );
    };

    handleDateChange = (date) => {
        this.setState({ date, error: null });
    };

    nextStep = () => {
        const step = this.getCurrentStep();
        const nextStep = step.nextStep();

        this.moveToStep(nextStep);
        step.nextAction();
    };

    onBack = () => {
        const step = this.getCurrentStep();

        if ((step.number - 1) <= 1) {
            this.setState({
                selectedFavourite: null,
                error: null,
                currentStep: 1
            });
        } else {
            this.setState({
                currentStep: this.state.currentStep - 1
            });
        }
    };

    renderFavourites = () => {
        if (this.state.selectedFavourite) {
            return null;
        }

        return (
            <FavouriteList favourites={ this.props.favourites } onSelect={ this.selectFavourite } onDelete={ this.deleteFavourite } />);
    };

    renderCalendar = () => {
        const { selectedFavourite, date } = this.state;
        const search = selectedFavourite ? _getSearchFromFavourite(selectedFavourite) : null;

        return (
            <Calendar tabIndex="0"
                      selected={ date }
                      onChange={ this.handleDateChange }
                      min={ search.getMinDate() }
                      max={ search.getMaxDate() } />
        );
    };

    getCurrentStep () {
        return _.find(this.steps(), ['number', this.state.currentStep]);
    }

    moveToStep (name) {
        const targetStep = _.find(this.steps(), ['name', name]);

        if (targetStep) {
            this.setState({
                currentStep: targetStep.number
            });
        }
    }

    renderFooter () {
        const { onCancel, isDesktop } = this.props;
        const step = this.getCurrentStep();

        if (!step || step.number > this.steps().length || (step.number === 1 && !isDesktop)) {
            return null;
        }

        return (
            <div>
                { isDesktop && (
                    <Button
                        bsStyle="default"
                        onClick={ onCancel }
                        className="close action-btn action-btn--secondary pull-left"
                    >
                        { t('button.text.close') }
                    </Button>
                ) }
                <span className="pull-right">
                    { (step.number > 1 && step.number <= this.steps().length) && (
                        <div>
                            <Button
                                bsStyle="default"
                                onClick={ this.onBack }
                                className="btn-md btn-primary previous"
                            >
                                { t('global.back') }
                            </Button>
                            <Button
                                bsStyle="default"
                                onClick={ this.nextStep }
                                className="btn-md btn-primary continue"
                                disabled={ step.nextDisabled(this.state) }
                            >
                                { t(step.continueTitle) }
                            </Button>
                        </div>
                    ) }
                </span>
            </div>
        );
    }

    render () {
        const { error, currentStep } = this.state;

        const modalProps = {
            titleText: 'favourites.modal.title',
            className: 'modal--xs',
            show: true,
            footer: this.renderFooter(),
            hideContinue: !this.props.isDesktop && currentStep === 1,
            onClose: this.props.onCancel,
        };

        return (
            <BaseModal { ...modalProps }>
              { error && <BAlert className="margin-bottom--sm" type="danger">{ t(`favourites.modal.error.${error.name}`) }</BAlert> }
              <SteppableList steps={ this.steps() }
                             currentStep={ currentStep } />
            </BaseModal>
        );
    }
}

export default compose(
    withRouter,
    connectToStores([ DimensionsStore ], _getStateFromStores)
)(FavouritesModal);



// WEBPACK FOOTER //
// ./src/components/favourites/FavouritesModal.js